import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, X, Megaphone, Calendar, FileText, Loader } from "lucide-react";
import PushNotificationsCard from "@/components/PushNotificationsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
        setNotifs(data);
        await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleClick = (n) => {
    if (n.link_type === "post") navigate("/app/feed");
    else if (n.link_type === "event") navigate("/app/events");
    else if (n.link_type === "room") navigate(`/app/salas/${n.link_id}`);
    else navigate("/app/feed");
  };

  const getIcon = (n) => {
    if (n.link_type === "event") return <Calendar size={18} className="text-[#F0A82C]" />;
    if (n.link_type === "post") return <FileText size={18} className="text-[#6B9070]" />;
    return <Megaphone size={18} className="text-[#E48D57]" />;
  };

  const timeAgo = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `Hace ${mins} min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `Hace ${days}d`;
  };

  return (
    <div data-testid="notifications-page" className="max-w-lg mx-auto px-4 py-6">
      <h1 className="font-heading text-2xl text-[#5B3E2E] mb-5">Notificaciones</h1>

      <PushNotificationsCard />

      {loading ? (
        <div className="flex justify-center py-12"><Loader size={24} className="text-[#E48D57] animate-spin" /></div>
      ) : notifs.length === 0 ? (
        <div className="text-center py-16 bg-[#FDFAF4] rounded-3xl border border-[#5B3E2E]/8">
          <Bell size={28} className="text-[#5B3E2E]/15 mx-auto mb-2" />
          <p className="text-sm text-[#5B3E2E]/40">No hay notificaciones</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifs.map((n, i) => (
            <button
              key={n.id}
              data-testid={`notif-${n.id}`}
              onClick={() => handleClick(n)}
              className={`w-full text-left p-4 rounded-2xl border transition-all hover:-translate-y-0.5 animate-slide-up ${
                n.read ? "bg-[#FDFAF4] border-[#5B3E2E]/6" : "bg-[#E48D57]/5 border-[#E48D57]/15"
              }`}
              style={{ animationDelay: `${i * 0.05}s`, opacity: 0, animationFillMode: "forwards" }}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#FDFAF4] flex items-center justify-center flex-shrink-0 border border-[#5B3E2E]/8">
                  {getIcon(n)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#5B3E2E]">{n.title}</p>
                  <p className="text-xs text-[#5B3E2E]/50 mt-0.5 line-clamp-2">{n.content}</p>
                  <p className="text-[10px] text-[#5B3E2E]/30 mt-1">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <div className="w-2 h-2 rounded-full bg-[#E48D57] mt-2 flex-shrink-0" />}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
